import React, { useEffect, useMemo, useState } from 'react'
import { Modal, Button, Form, Table, Alert } from 'react-bootstrap'
import {
  calculateActualAge,
  dateFin,
  isOld,
  isOverMonthOld,
} from '../services/helpfunctions'
import {
  getTournamentFromCandidate,
  getU21TournamentEligibility,
} from '../services/u21utils'

const RemovePlayersPaintModal = ({
  show,
  onHide,
  sourceCandidate,
  sourceEntries,
  onRemove,
}) => {
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [isPainting, setIsPainting] = useState(false)
  const [paintToSelected, setPaintToSelected] = useState(true)
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (!show) return

    setSelectedIds(new Set())
    setIsPainting(false)
    setPaintToSelected(true)
    setError('')
    setIsSubmitting(false)
  }, [show])

  useEffect(() => {
    if (!show) return

    const stopPainting = () => setIsPainting(false)
    window.addEventListener('mouseup', stopPainting)

    return () => {
      window.removeEventListener('mouseup', stopPainting)
    }
  }, [show])

  const rows = useMemo(() => {
    const tournamentInfo = getTournamentFromCandidate(sourceCandidate)

    return (sourceEntries || []).map(entry => {
      const player = entry.player || {}
      const age = calculateActualAge(player)

      let eligibility = '-'
      if (sourceCandidate?.TeamType === 'MJ') {
        eligibility = 'MJ'
      } else if (tournamentInfo) {
        const u21 = getU21TournamentEligibility(
          age.years,
          age.days,
          tournamentInfo.type,
          tournamentInfo.tournament
        )
        eligibility = u21?.text || '-'
      }

      const key = String(entry._id || player.playerId)

      return {
        key,
        name: `${player.firstName || ''} ${player.lastName || ''}`.trim(),
        ageText: `${age.years}.${age.days}`,
        eligibility,
        old: isOld(player.dateUpdated),
        monthOld: isOverMonthOld(player.dateUpdated),
        updatedAtText: player.dateUpdated ? dateFin(player.dateUpdated) : '',
      }
    })
  }, [sourceEntries, sourceCandidate])

  const applyPaint = (rowKey, select) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (select) {
        next.add(rowKey)
      } else {
        next.delete(rowKey)
      }
      return next
    })
  }

  const handleRowMouseDown = rowKey => {
    const shouldSelect = !selectedIds.has(rowKey)
    setPaintToSelected(shouldSelect)
    setIsPainting(true)
    applyPaint(rowKey, shouldSelect)
  }

  const handleRowMouseEnter = rowKey => {
    if (!isPainting) return
    applyPaint(rowKey, paintToSelected)
  }

  const handleSubmit = async event => {
    event.preventDefault()
    setError('')

    const ids = Array.from(selectedIds)
    if (ids.length === 0) {
      setError('Valitse vähintään yksi pelaaja poistettavaksi')
      return
    }

    const ok = window.confirm(
      `Poistetaanko varmasti ${ids.length} pelaajaa listalta?`
    )
    if (!ok) return

    setIsSubmitting(true)
    try {
      await onRemove({ selectedIds: ids })
      onHide()
    } catch (err) {
      setError(err?.message || 'Pelaajien poisto epäonnistui')
    } finally {
      setIsSubmitting(false)
    }
  }

  const selectedCount = selectedIds.size
  const remainingCount = Math.max(rows.length - selectedCount, 0)

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header
          closeButton
          closeVariant="white"
          style={{
            backgroundColor: '#23272A',
            color: '#FFFFFF',
            borderBottom: '1px solid #3A3F44',
          }}
        >
          <Modal.Title>Poista pelaajia listalta</Modal.Title>
        </Modal.Header>

        <Modal.Body
          style={{
            backgroundColor: '#2C2F33',
            color: '#FFFFFF',
          }}
        >
          {error && <Alert variant="danger">{error}</Alert>}

          <div
            className="mb-2"
            style={{ fontSize: '0.9rem', color: '#B9BBBE' }}
          >
            Maalaa hiirellä poistettavat rivit harmaaksi. Maalaus saman rivin
            yli palauttaa sen takaisin aktiiviseksi.
          </div>

          <div
            className="mb-2"
            style={{ fontSize: '0.9rem', color: '#DCDDDE' }}
          >
            Jäljelle jää:{' '}
            <strong style={{ color: '#2ecc71' }}>{remainingCount}</strong> |
            Poistettavat:{' '}
            <strong style={{ color: '#ff8a80' }}>{selectedCount}</strong>
          </div>

          <div
            style={{
              maxHeight: '420px',
              overflowY: 'auto',
              border: '1px solid #3A3F44',
              borderRadius: '4px',
            }}
          >
            <Table size="sm" hover variant="dark" className="mb-0">
              <thead
                style={{
                  position: 'sticky',
                  top: 0,
                  background: '#23272A',
                  zIndex: 1,
                }}
              >
                <tr>
                  <th>Nimi</th>
                  <th>Ikä</th>
                  <th>Edustuskelpoisuus</th>
                </tr>
              </thead>
              <tbody style={{ userSelect: isPainting ? 'none' : 'auto' }}>
                {rows.map(row => {
                  const isSelected = selectedIds.has(row.key)
                  const cellStyle = {
                    backgroundColor: isSelected ? '#9AA0A8' : '#252A31',
                    color: isSelected ? '#111418' : '#FFFFFF',
                    borderColor: '#3A3F44',
                    fontWeight: isSelected ? 600 : 400,
                  }

                  return (
                    <tr
                      key={row.key}
                      onMouseDown={() => handleRowMouseDown(row.key)}
                      onMouseEnter={() => handleRowMouseEnter(row.key)}
                      onMouseUp={() => setIsPainting(false)}
                      style={{
                        cursor: 'crosshair',
                        opacity: row.monthOld ? 0.85 : 1,
                        boxShadow: isSelected
                          ? 'inset 0 0 0 1px #C2C7CE'
                          : 'none',
                        borderLeft: row.old
                          ? '3px solid #e74c3c'
                          : '3px solid #2ecc71',
                      }}
                      title={
                        row.updatedAtText
                          ? `Paivitetty ${row.updatedAtText}`
                          : ''
                      }
                    >
                      <td style={cellStyle}>{row.name || '-'}</td>
                      <td style={cellStyle}>{row.ageText}</td>
                      <td style={cellStyle}>{row.eligibility}</td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          </div>
        </Modal.Body>

        <Modal.Footer
          style={{
            backgroundColor: '#23272A',
            borderTop: '1px solid #3A3F44',
          }}
        >
          <Button variant="secondary" onClick={onHide} disabled={isSubmitting}>
            Peruuta
          </Button>
          <Button type="submit" variant="danger" disabled={isSubmitting}>
            {isSubmitting ? 'Poistetaan...' : 'Poista'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}

export default RemovePlayersPaintModal
